import {
  AvatarData,
  Badge,
  Button,
  GridCellLink,
  GridCellText,
  Icon,
  SvgIcon,
  type TableColumn,
  Tooltip,
} from '@civilgrid/ui/components';
import { cn, getNonNullUserName, OPERATOR_TYPES } from '@civilgrid/ui/utils';
import { Link } from '@tanstack/react-router';
import { projectTypeLabels } from '~/common/projectUtils';
import type { Member, Project } from '~/common/types';
import { ProjectGeometryType } from '~/common/types';
import { formatAddress, formatUTCDate } from '../../utils';

/** Type column: Draft, Project Lite, Project, Project Plus (proposal = Project Lite) */
const BILLING_TIER_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'proposal', label: 'Project Lite' },
  { value: 'project', label: 'Project' },
  { value: 'projectPlus', label: 'Project Plus' },
];

const BILLING_TIER_LABELS: Record<string, string> = {
  draft: 'Draft',
  proposal: 'Project Lite',
  project: 'Project',
  projectPlus: 'Project Plus',
};

/** Badge color per Type: Grey, Blue, Orange, Orange */
const BILLING_TIER_COLORS: Record<string, 'grey' | 'blue' | 'orange'> = {
  draft: 'grey',
  proposal: 'blue',
  project: 'orange',
  projectPlus: 'orange',
};

export type ProjectColumnFilterOptions = {
  /** Org members for Project Manager filter dropdown (same pattern as Assigned To in drf-app). */
  orgMembers?: Member[];
};

/**
 * Cell that links to project details. Uses current org from rootStore for route.
 */
function ProjectLinkCell({
  projectId,
  describedBy,
  children,
}: {
  projectId: string;
  describedBy?: string;
  children: React.ReactNode;
}) {
  return (
    <GridCellLink asChild>
      <Link
        to="/projects/$projectId"
        params={{ projectId }}
        aria-describedby={describedBy}
        data-testid={`home-projects-table-link-${projectId}`}
      >
        {children}
      </Link>
    </GridCellLink>
  );
}

const bookmarkedDescriptionId = (projectId: string) => `home-projects-table-bookmarked-${projectId}`;

function formatGeometryType(value: ProjectGeometryType | string | undefined): string {
  if (!value) return '—';
  const s = String(value);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/** Project row may include API-only fields (scope, client, projectVicinity, projectArea, linear.lengthInMiles) */
type ProjectRow = Project & {
  scope?: string;
  client?: string;
  projectVicinity?: string;
  projectArea?: number;
  linear?: { lengthInMiles?: number };
};

export function getProjectColumns(
  filterOptions?: ProjectColumnFilterOptions,
  onShowOnMap?: (project: Project) => void,
): TableColumn<Project>[] {
  const projectManagerOptions = (filterOptions?.orgMembers ?? [])
    .filter((m) => m.user?.status === 'active')
    .map((m) => ({
      value: m.user.id,
      label:
        [m.user.firstName, m.user.lastName].filter(Boolean).join(' ') ||
        getNonNullUserName(m.user.name) ||
        m.user.email,
    }));

  return [
    {
      id: 'projectId',
      accessorKey: 'projectId',
      header: 'ID',
      cell: (info) => <GridCellText>{info.getValue<Project['projectId']>() ?? '—'}</GridCellText>,
      enableSorting: true,
      enableColumnFilter: true,
      size: 100,
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Project Name',
      cell: (info) => {
        const p = info.row.original;
        return (
          <div className="relative flex w-full min-w-0 items-center">
            <div
              className={cn(
                'flex min-w-0 flex-1 items-center gap-0.5',
                onShowOnMap && 'group-data-[hovered=true]/row:pr-7',
              )}
            >
              {p.bookmarked && <Icon icon="bookmarkFilled" ariaLabel="Bookmarked" className="w-3 h-3 shrink-0" />}
              <ProjectLinkCell projectId={p.id} describedBy={p.bookmarked ? bookmarkedDescriptionId(p.id) : undefined}>
                {info.getValue<Project['name']>() || '—'}
              </ProjectLinkCell>
              {/* Hidden from reading mode, where the icon is read; describes the link so Tab focus reads it too. */}
              {p.bookmarked && (
                <span id={bookmarkedDescriptionId(p.id)} hidden>
                  Bookmarked
                </span>
              )}
            </div>
            {onShowOnMap && (
              <div className="absolute inset-y-0 right-0 flex items-center">
                <Tooltip text="Click to zoom the map to this project." asChild>
                  <Button
                    size="icon"
                    variant="hollow"
                    type="button"
                    aria-label={`Show ${p.name || 'project'} on map`}
                    className="shrink-0 opacity-0 transition-colors group-data-[hovered=true]/row:opacity-100 focus-visible:opacity-100 text-grey-50 hover:text-grey-70"
                    data-testid={`home-projects-table-show-on-map-${p.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onShowOnMap(p);
                    }}
                  >
                    <SvgIcon icon="showOnMap" size={14} />
                  </Button>
                </Tooltip>
              </div>
            )}
          </div>
        );
      },
      enableHiding: false,
      enableSorting: true,
      enableColumnFilter: true,
      size: 180,
    },
    {
      id: 'billingTier',
      accessorKey: 'billingTier',
      header: 'Type',
      cell: (info) => {
        const tier = String(info.getValue<Project['billingTier']>() ?? '');
        const label = BILLING_TIER_LABELS[tier] ?? '—';
        const color = BILLING_TIER_COLORS[tier] ?? 'grey';
        return (
          <Badge color={color}>
            <GridCellText>{label}</GridCellText>
          </Badge>
        );
      },
      enableSorting: true,
      enableColumnFilter: true,
      size: 100,
      meta: {
        filterValueConfig: { type: 'select', options: BILLING_TIER_OPTIONS },
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
      },
    },
    {
      id: 'projectManagerId',
      accessorKey: 'projectManagerId',
      header: 'Project Manager',
      cell: (info) => {
        const projectManager = info.row.original.projectManager;
        if (!projectManager) return <GridCellText>—</GridCellText>;
        const pm = {
          ...projectManager,
          name: getNonNullUserName(projectManager.name),
        };
        if (!pm?.name && !pm?.email) return <GridCellText>—</GridCellText>;
        return <AvatarData user={{ name: pm.name || pm.email || '', email: pm.email || '' }} size="small" />;
      },
      enableSorting: true,
      enableColumnFilter: true,
      size: 155,
      meta: {
        filterValueConfig: { type: 'select', options: projectManagerOptions },
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
      },
    },
    {
      id: 'projectArea',
      accessorKey: 'projectArea',
      header: 'Area',
      cell: (info) => {
        const area = (info.row.original as ProjectRow).projectArea;
        if (area == null) return <GridCellText>—</GridCellText>;
        return <GridCellText>{Number(area).toLocaleString(undefined, { maximumFractionDigits: 2 })} ac</GridCellText>;
      },
      enableSorting: true,
      enableColumnFilter: true,
      size: 100,
      meta: {
        filterValueConfig: { type: 'text' },
        filterOperatorTypeConfig: OPERATOR_TYPES.COMPARISON,
      },
    },
    {
      id: 'linear.lengthInMiles',
      accessorKey: 'linear.lengthInMiles',
      header: 'Length',
      cell: (info) => {
        const len = (info.row.original as ProjectRow).linear?.lengthInMiles;
        if (len == null) return <GridCellText>—</GridCellText>;
        return <GridCellText>{Number(len).toLocaleString(undefined, { maximumFractionDigits: 2 })} mi</GridCellText>;
      },
      enableSorting: true,
      enableColumnFilter: true,
      size: 90,
      meta: {
        filterValueConfig: { type: 'text' },
        filterOperatorTypeConfig: OPERATOR_TYPES.COMPARISON,
      },
    },
    {
      id: 'geometryType',
      accessorKey: 'geometryType',
      header: 'Geometry',
      cell: (info) => <GridCellText>{formatGeometryType(info.row.original.geometryType)}</GridCellText>,
      enableSorting: true,
      enableColumnFilter: true,
      size: 100,
      meta: {
        filterValueConfig: {
          type: 'select',
          options: [
            { value: ProjectGeometryType.Polygon, label: 'Polygon' },
            { value: ProjectGeometryType.Linear, label: 'Linear' },
            { value: ProjectGeometryType.Circle, label: 'Circle' },
          ],
        },
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
      },
    },
    {
      id: 'billingCode',
      accessorKey: 'billingCode',
      header: 'Billing Code 1',
      cell: (info) => <GridCellText>{info.getValue<Project['billingCode']>() || '—'}</GridCellText>,
      enableSorting: true,
      enableColumnFilter: true,
      size: 120,
    },
    {
      id: 'type',
      accessorKey: 'type',
      header: 'Category',
      cell: (info) => {
        const value = info.row.original.type ?? '';
        const label = value ? projectTypeLabels[value]?.label : null;
        return <GridCellText>{label || '—'}</GridCellText>;
      },
      enableSorting: true,
      enableColumnFilter: true,
      size: 120,
      meta: {
        filterValueConfig: {
          type: 'select',
          options: Object.values(projectTypeLabels).map(({ value, label }) => ({ value, label })),
        },
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
        defaultVisible: false,
      },
    },
    {
      id: 'address',
      accessorKey: 'address',
      header: 'Address',
      cell: (info) => <GridCellText>{formatAddress(info.row.original.address2)}</GridCellText>,
      enableSorting: true,
      enableColumnFilter: true,
      size: 200,
      meta: { defaultVisible: false },
    },
    {
      id: 'startDate',
      accessorKey: 'startDate',
      header: 'Project Start Date',
      cell: (info) => <GridCellText>{formatUTCDate(info.row.original.startDate?.toISO())}</GridCellText>,
      enableSorting: true,
      enableColumnFilter: true,
      size: 140,
      meta: {
        filterValueConfig: { type: 'date' },
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
      },
    },
    {
      id: 'endDate',
      accessorKey: 'endDate',
      header: 'Project End Date',
      cell: (info) => <GridCellText>{formatUTCDate(info.row.original.endDate?.toISO())}</GridCellText>,
      enableSorting: true,
      enableColumnFilter: true,
      size: 135,
      meta: {
        filterValueConfig: { type: 'date' },
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
      },
    },
    {
      id: 'constructionStartDate',
      accessorKey: 'constructionStartDate',
      header: 'Construction Start Date',
      cell: (info) => <GridCellText>{formatUTCDate(info.row.original.constructionStartDate?.toISO())}</GridCellText>,
      enableSorting: true,
      enableColumnFilter: true,
      size: 175,
      meta: {
        filterValueConfig: { type: 'date' },
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
      },
    },
    {
      id: 'constructionEndDate',
      accessorKey: 'constructionEndDate',
      header: 'Construction End Date',
      cell: (info) => <GridCellText>{formatUTCDate(info.row.original.constructionEndDate?.toISO())}</GridCellText>,
      enableSorting: true,
      enableColumnFilter: true,
      size: 170,
      meta: {
        filterValueConfig: { type: 'date' },
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
      },
    },
    {
      id: 'client',
      accessorKey: 'client',
      header: 'Client',
      cell: (info) => <GridCellText>{(info.row.original as ProjectRow).client || '—'}</GridCellText>,
      enableSorting: true,
      enableColumnFilter: true,
      size: 125,
      meta: { defaultVisible: false },
    },
    {
      id: 'billingCode2',
      accessorKey: 'billingCode2',
      header: 'Billing Code 2',
      cell: (info) => <GridCellText>{info.getValue<Project['billingCode2']>() || '—'}</GridCellText>,
      enableSorting: true,
      enableColumnFilter: true,
      size: 120,
      meta: { defaultVisible: false },
    },
    {
      id: 'scope',
      accessorKey: 'scope',
      header: 'Scope',
      cell: (info) => <GridCellText>{(info.row.original as ProjectRow).scope || '—'}</GridCellText>,
      enableSorting: true,
      enableColumnFilter: true,
      size: 200,
      meta: { defaultVisible: false },
    },
    {
      id: 'projectVicinity',
      accessorKey: 'projectVicinity',
      header: 'Project Vicinity',
      cell: (info) => <GridCellText>{(info.row.original as ProjectRow).projectVicinity || '—'}</GridCellText>,
      enableSorting: true,
      enableColumnFilter: true,
      size: 150,
      meta: { defaultVisible: false },
    },
    {
      id: 'address2.crossStreets',
      accessorKey: 'address2.crossStreets',
      header: 'Cross Streets',
      cell: (info) => <GridCellText>{info.row.original.address2?.crossStreets || '—'}</GridCellText>,
      enableSorting: true,
      enableColumnFilter: true,
      size: 150,
      meta: { defaultVisible: false },
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: 'Created Date',
      cell: (info) => <GridCellText>{formatUTCDate(info.row.original.createdAt?.toISO())}</GridCellText>,
      enableSorting: true,
      enableColumnFilter: true,
      size: 135,
      meta: {
        filterValueConfig: { type: 'date' },
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
      },
    },
  ];
}
